import { gql } from '@apollo/client';
import { cx } from '@orn/shared-web/utils/cx';

import { CalloutType } from '../../__generated__/globalTypes';
import Entity from '../entity';

import {
  HighCreditUtilizationCalloutCard,
  LowBalanceCalloutCard,
  UnhealthyBankConnectionCalloutCard,
  UnusualCumulativeSpendCalloutCard,
  UnusualTransactionCalloutCard,
  UnverifiedPhoneCalloutCard,
} from './cards';

import type { CalloutDetailsFragment } from './__generated__/CalloutCard.gql';

// Minimal transaction fragment for callout card display
const CalloutTransactionFragment = gql`
  fragment CalloutTransaction on Transaction {
    id
    amount
    date
    description
    merchant {
      id
      name
      logoUrl
    }
    financialAccount {
      id
      name
      mask
      subtype
      ...EntityAvatar
      bank {
        id
        name
      }
    }
  }
  ${Entity.Avatar.Fragment}
`;

const CalloutFragment = gql`
  fragment CalloutDetails on Callout {
    id
    type
    priority
    metadata
    dismissed
    feedbackRating
    feedbackNotes
    eventDate
    transaction {
      ...CalloutTransaction
    }
    merchant {
      id
      ...EntityAvatar
    }
    financialAccount {
      id
      name
      mask
      subtype
      ...EntityAvatar
      bank {
        id
        name
        ...EntityAvatar
      }
    }
    bankConnection {
      id
      lastSyncedAt
      bank {
        id
        ...EntityAvatar
      }
    }
  }
  ${CalloutTransactionFragment}
  ${Entity.Avatar.Fragment}
`;

export type Callout = CalloutDetailsFragment;

interface ICalloutCardProps {
  callout: Callout;
  /**
   * Called when the user dismisses the callout (for UnusualTransaction callouts).
   * @param appropriate - true if helpful, false if not helpful, undefined if not asked
   */
  onDismiss?: (appropriate?: boolean) => void;
  className?: string;
  onClick?: () => void;
  /**
   * 'full' shows transaction details (merchant, amount, date), used in carousel.
   * 'compact' shows description only, used in slideout.
   * @default 'full'
   */
  variant?: 'full' | 'compact';
}

export const CALLOUT_CARD_HEIGHT = 200;
// Static string so Tailwind's JIT scanner can detect the class at build time.
export const CALLOUT_CARD_HEIGHT_CLASS = 'h-[200px]';

const CalloutCard = ({ callout, onDismiss, className, onClick, variant = 'full' }: ICalloutCardProps) => {
  const resolvedClassName = variant === 'full' ? cx(CALLOUT_CARD_HEIGHT_CLASS, className) : className;

  if (callout.type === CalloutType.HighCreditUtilization) {
    return (
      <HighCreditUtilizationCalloutCard
        callout={callout}
        onDismiss={onDismiss}
        className={resolvedClassName}
        onClick={onClick}
        variant={variant}
      />
    );
  }

  if (callout.type === CalloutType.LowBalance) {
    return (
      <LowBalanceCalloutCard
        callout={callout}
        onDismiss={onDismiss}
        className={resolvedClassName}
        onClick={onClick}
        variant={variant}
      />
    );
  }

  if (callout.type === CalloutType.UnusualTransaction) {
    return (
      <UnusualTransactionCalloutCard
        callout={callout}
        onDismiss={onDismiss}
        className={resolvedClassName}
        onClick={onClick}
        variant={variant}
      />
    );
  }

  if (callout.type === CalloutType.UnhealthyBankConnection) {
    return <UnhealthyBankConnectionCalloutCard callout={callout} className={resolvedClassName} />;
  }

  if (callout.type === CalloutType.UnusualCumulativeSpend) {
    return (
      <UnusualCumulativeSpendCalloutCard
        callout={callout}
        onDismiss={onDismiss}
        className={resolvedClassName}
        onClick={onClick}
        variant={variant}
      />
    );
  }

  if (callout.type === CalloutType.UnverifiedPhone) {
    return <UnverifiedPhoneCalloutCard callout={callout} onDismiss={onDismiss} className={resolvedClassName} />;
  }

  return null;
};

CalloutCard.Fragment = CalloutFragment;

export default CalloutCard;
